import { Box, Button as MuiButton, Checkbox, FormControlLabel, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import useEditSubstanceForm, { EditSubstanceFormData } from '../../hooks/useEditSubstanceForm';
import { useState } from 'react';
import useNewStudyForm, { NewStudyFormData } from '../../hooks/useNewStudyForm';
import { useAppSelector } from '../../../../../../hooks/reduxHooks';
import { SubstanceStudyCard } from '../SubstanceStudyCard';
import { DrawerSvccCargando } from '../../../../components/drawer/DrawerSvcc';
import { enqueueSnackbar } from 'notistack';
import { isStudiesTabEnabled } from '../../helpers';
import {
    DrawerSvccCuerpo,
    DrawerSvccEncabezado,
    DrawerSvccPie,
    DrawerSvccSeccion
} from '../../../../components/drawer/DrawerSvcc';
import CamposSvcc from '../../../../components/drawer/CamposSvcc';
import PestanasDrawerSvcc from '../../../../components/drawer/PestanasDrawerSvcc';

/** Secciones de la pestaña Sustancia, por `name` de la configuración de campos. */
const SECCIONES_SUSTANCIA = [
    { clave: 'sustancia', nombres: ['substance', 'workPositionAndSector', 'comercialName'] },
    {
        clave: 'uso',
        nombres: ['usageOrigin', 'employmentMethods', 'substanceAnualQuantity', 'measureUnit']
    },
    {
        clave: 'informacion',
        nombres: ['report', 'trainingProgram', 'studiesAndReplacementPosibility', 'specialLicense']
    },
    { clave: 'proteccion', nombres: ['preventiveMeasures', 'protectiveEquipment'] }
];

const botonSecundario = { borderRadius: '8px', textTransform: 'none', fontWeight: 600 } as const;

const SubstanceInformationDrawer = ({
    setOpen,
    selectedSubstance,
    translate,
    currentTab,
    handleTabChange,
    onlyVisual
}: any) => {
    const [showStudyForm, setShowStudysForm] = useState(false);
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const carcinogenicReport = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport
    );

    const carcinogenicParametricData = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicParametricData
    );

    const selectedSubstanceId: number = selectedSubstance?.id;

    const handleCloseDrawer = () => {
        setOpen('');
    };

    const {
        formData: editSubstanceFormData,
        errors: editSubstanceErrors,
        editSubstanceFieldsConfig,
        handleChange: editSubstanceHandleChange,
        handleEditSubstance,
        isEditingSubstance,
        noComercialNameChecked,
        setNoComercialNameChecked
    } = useEditSubstanceForm({
        selectedSubstance,
        selectedSubstanceId,
        selectedCarcinogenicReportId,
        handleTabChange
    });

    const {
        formData: newStudyFormData,
        errors: newStudyErrors,
        newStudyFieldsConfig,
        handleChange: newStudyHandleChange,
        handleSubmitStudy,
        handleDeleteSubstanceIconClick,
        isCreatingNewSubstanceStudy,
        isDeletingSubstanceStudy
    } = useNewStudyForm({ selectedSubstanceId, selectedCarcinogenicReportId });

    const getFrequencyTypeDescriptionById = (id: number): string | null => {
        if (!carcinogenicParametricData?.tiposFrecuencia || !id) {
            return null;
        }
        const item = carcinogenicParametricData.tiposFrecuencia.find(
            (frequencyType) => frequencyType.id === id
        );
        return item ? item.descripcion : null;
    };

    const getStudyTypeName = (studyId: number) => {
        if (!studyId) return '';
        const studyType = carcinogenicParametricData?.tiposEstudio?.find(
            (studyType) => studyType.id === studyId
        );
        return studyType ? studyType.descripcion : '';
    };

    //this help to maintaing real time values without depending on local management
    const currentSubstanceItem = carcinogenicReport.sustancias.find(
        (item: any) => item?.id === selectedSubstance.id
    );

    const toggleSinNombreComercial = () => {
        if (onlyVisual) return;
        if (noComercialNameChecked) {
            editSubstanceHandleChange('comercialName', '');
            setNoComercialNameChecked(false);
        } else {
            editSubstanceHandleChange('comercialName', 'No Aplica');
            setNoComercialNameChecked(true);
        }
    };

    const guardarEstudios = () => {
        if (currentSubstanceItem?.estudios?.length === 0) {
            enqueueSnackbar(translate('newStudyForm.enqueues.common.noStudies'), {
                variant: 'error'
            });
        } else {
            handleCloseDrawer();
        }
    };

    const estudios = currentSubstanceItem?.estudios ?? [];

    return (
        <>
            <DrawerSvccEncabezado
                titulo={
                    currentTab === 0
                        ? translate('editSubstanceForm.titles.substance')
                        : translate('editSubstanceForm.titles.studies')
                }
                paso={translate('editSubstanceForm.drawer.paso')}
                onCerrar={handleCloseDrawer}
            >
                <PestanasDrawerSvcc
                    valor={currentTab}
                    onCambio={handleTabChange}
                    pestanas={[
                        {
                            valor: 0,
                            etiqueta: translate('editSubstanceForm.drawer.pestanaSustancia')
                        },
                        {
                            valor: 1,
                            etiqueta: translate('editSubstanceForm.drawer.pestanaEstudios'),
                            deshabilitada: !isStudiesTabEnabled(selectedSubstance) && !onlyVisual
                        }
                    ]}
                />
            </DrawerSvccEncabezado>

            <DrawerSvccCuerpo>
                {currentTab === 0 &&
                    SECCIONES_SUSTANCIA.map(({ clave, nombres }) => (
                        <DrawerSvccSeccion
                            key={clave}
                            titulo={translate(`editSubstanceForm.drawer.secciones.${clave}`)}
                        >
                            <CamposSvcc
                                config={editSubstanceFieldsConfig}
                                nombres={nombres}
                                formData={
                                    editSubstanceFormData as EditSubstanceFormData &
                                        Record<string, any>
                                }
                                errors={editSubstanceErrors}
                                onChange={editSubstanceHandleChange}
                                onlyVisual={onlyVisual}
                                debajoDe={(nombre) =>
                                    nombre === 'comercialName' && (
                                        <FormControlLabel
                                            sx={{ mt: 0.5, ml: -0.5 }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={noComercialNameChecked}
                                                    onChange={toggleSinNombreComercial}
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {translate(
                                                        'editSubstanceForm.drawer.sinNombreComercial'
                                                    )}
                                                </Typography>
                                            }
                                        />
                                    )
                                }
                            />
                        </DrawerSvccSeccion>
                    ))}

                {currentTab === 1 && (
                    <>
                        <DrawerSvccSeccion
                            titulo={translate('editSubstanceForm.drawer.secciones.estudios')}
                        >
                            {estudios.length ? (
                                <Box display="grid" gap={1.25}>
                                    {estudios.map((study: any) => (
                                        <SubstanceStudyCard
                                            key={study?.id}
                                            type={getStudyTypeName(study?.id)}
                                            description={study?.detalle}
                                            frequency={getFrequencyTypeDescriptionById(
                                                study?.idTipoFrecuencia
                                            )}
                                            frequencyQuantity={study?.cantidad}
                                            handleDeleteClick={() => {
                                                if (onlyVisual) return;
                                                handleDeleteSubstanceIconClick(study?.id);
                                            }}
                                            onlyVisual={onlyVisual}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ color: '#6B777C' }}>
                                    {translate('editSubstanceForm.drawer.sinEstudios')}
                                </Typography>
                            )}
                            {!onlyVisual && !showStudyForm && (
                                <MuiButton
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowStudysForm(true)}
                                    sx={{ ...botonSecundario, mt: 1.75 }}
                                >
                                    {translate('newStudyForm.buttons.new')}
                                </MuiButton>
                            )}
                        </DrawerSvccSeccion>

                        {showStudyForm && (
                            <DrawerSvccSeccion
                                titulo={translate(
                                    'editSubstanceForm.drawer.secciones.nuevoEstudio'
                                )}
                            >
                                <CamposSvcc
                                    config={newStudyFieldsConfig}
                                    formData={
                                        newStudyFormData as NewStudyFormData & Record<string, any>
                                    }
                                    errors={newStudyErrors}
                                    onChange={newStudyHandleChange}
                                />
                                <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
                                    <MuiButton
                                        variant="text"
                                        onClick={() => setShowStudysForm(false)}
                                        sx={botonSecundario}
                                    >
                                        {translate('newStudyForm.buttons.cancel')}
                                    </MuiButton>
                                    <MuiButton
                                        variant="outlined"
                                        onClick={handleSubmitStudy}
                                        sx={botonSecundario}
                                    >
                                        {translate('newStudyForm.buttons.addStudy')}
                                    </MuiButton>
                                </Box>
                            </DrawerSvccSeccion>
                        )}
                    </>
                )}
            </DrawerSvccCuerpo>

            <DrawerSvccPie
                onCancelar={handleCloseDrawer}
                onConfirmar={currentTab === 0 ? handleEditSubstance : guardarEstudios}
                textoCancelar={translate('editSubstanceForm.buttons.cancel')}
                textoConfirmar={translate('editSubstanceForm.buttons.save')}
                nota={
                    onlyVisual
                        ? undefined
                        : currentTab === 0
                          ? translate('editSubstanceForm.drawer.camposObligatorios')
                          : // "Cargá al menos un estudio" sólo mientras no haya ninguno.
                            estudios.length
                            ? undefined
                            : translate('editSubstanceForm.drawer.notaEstudios')
                }
                sinConfirmar={onlyVisual}
            />
            <DrawerSvccCargando
                cargando={
                    isEditingSubstance || isCreatingNewSubstanceStudy || isDeletingSubstanceStudy
                }
            />
        </>
    );
};

export { SubstanceInformationDrawer };
